import { useState } from 'react';
import { motion } from 'framer-motion';
import { useConfig } from '../../context/ConfigContext';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import toast from 'react-hot-toast';
import api from '../../utils/api';

const { FiShield, FiKey, FiLock, FiEye, FiEyeOff, FiSave } = FiIcons;

const SecuritySettings = () => {
    const { config, updateConfig } = useConfig();
    const [adminCredentials, setAdminCredentials] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
    });
    const [showPasswords, setShowPasswords] = useState({
        current: false,
        new: false,
        confirm: false
    });
    const [securitySettings, setSecuritySettings] = useState({
        twoFactorEnabled: config.security?.twoFactorEnabled || false,
        recaptchaEnabled: config.security?.recaptchaEnabled || false,
        recaptchaSiteKey: config.security?.recaptchaSiteKey || '',
        recaptchaSecretKey: config.security?.recaptchaSecretKey || '',
        sessionTimeout: config.security?.sessionTimeout || 60,
        maxLoginAttempts: config.security?.maxLoginAttempts || 5,
        passwordMinLength: config.security?.passwordMinLength || 8,
        requireSpecialChars: config.security?.requireSpecialChars || true
    });

    const handlePasswordChange = async () => {
        if (!adminCredentials.currentPassword) {
            toast.error('Please enter current password');
            return;
        }

        if (adminCredentials.newPassword !== adminCredentials.confirmPassword) {
            toast.error('New passwords do not match');
            return;
        }

        if (adminCredentials.newPassword.length < 8) {
            toast.error('Password must be at least 8 characters long');
            return;
        }

        try {
            // Payload for API
            const payload = {
                currentPassword: adminCredentials.currentPassword,
                newPassword: adminCredentials.newPassword
            };

            // Call API using PUT method
            await api.put('/auth/profile', payload);

            toast.success('Admin password updated successfully');
            setAdminCredentials({ currentPassword: '', newPassword: '', confirmPassword: '' });

        } catch (error) {
            console.error('Password change error:', error);
            toast.error(error.response?.data?.message || 'Failed to update password');
        }
    };

    const handleSecuritySettingsUpdate = () => {
        updateConfig({
            security: securitySettings
        });
        toast.success('Security settings updated successfully');
    };

    const togglePasswordVisibility = (field) => {
        setShowPasswords(prev => ({
            ...prev,
            [field]: !prev[field]
        }));
    };

    return (
        <div className="space-y-8">
            {/* Admin Password Management */}
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700"
            >
                <div className="flex items-center space-x-3 mb-6">
                    <SafeIcon icon={FiKey} className="w-5 h-5 text-emerald-600" />
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        Admin Password Management
                    </h3>
                </div>

                <div className="grid md:grid-cols-3 gap-6">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Current Password
                        </label>
                        <div className="relative">
                            <input
                                type={showPasswords.current ? 'text' : 'password'}
                                value={adminCredentials.currentPassword}
                                onChange={(e) => setAdminCredentials(prev => ({ ...prev, currentPassword: e.target.value }))}
                                className="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                                placeholder="Enter current password"
                            />
                            <button
                                type="button"
                                onClick={() => togglePasswordVisibility('current')}
                                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                            >
                                <SafeIcon icon={showPasswords.current ? FiEyeOff : FiEye} className="w-4 h-4" />
                            </button>
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            New Password
                        </label>
                        <div className="relative">
                            <input
                                type={showPasswords.new ? 'text' : 'password'}
                                value={adminCredentials.newPassword}
                                onChange={(e) => setAdminCredentials(prev => ({ ...prev, newPassword: e.target.value }))}
                                className="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                                placeholder="Enter new password"
                            />
                            <button
                                type="button"
                                onClick={() => togglePasswordVisibility('new')}
                                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                            >
                                <SafeIcon icon={showPasswords.new ? FiEyeOff : FiEye} className="w-4 h-4" />
                            </button>
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Confirm New Password
                        </label>
                        <div className="relative">
                            <input
                                type={showPasswords.confirm ? 'text' : 'password'}
                                value={adminCredentials.confirmPassword}
                                onChange={(e) => setAdminCredentials(prev => ({ ...prev, confirmPassword: e.target.value }))}
                                className="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                                placeholder="Confirm new password"
                            />
                            <button
                                type="button"
                                onClick={() => togglePasswordVisibility('confirm')}
                                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                            >
                                <SafeIcon icon={showPasswords.confirm ? FiEyeOff : FiEye} className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                </div>

                <div className="mt-6">
                    <button
                        onClick={handlePasswordChange}
                        className="flex items-center space-x-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition-colors"
                    >
                        <SafeIcon icon={FiLock} className="w-4 h-4" />
                        <span>Update Password</span>
                    </button>
                </div>
            </motion.div>

            {/* Two-Factor Authentication */}
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700"
            >
                <div className="flex items-center space-x-3 mb-6">
                    <SafeIcon icon={FiShield} className="w-5 h-5 text-emerald-600" />
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        Two-Factor Authentication (2FA)
                    </h3>
                </div>

                <div className="space-y-4">
                    <div className="flex items-center justify-between">
                        <div>
                            <h4 className="font-medium text-gray-900 dark:text-white">Enable 2FA</h4>
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                                Add an extra layer of security to admin accounts
                            </p>
                        </div>
                        <label className="flex items-center">
                            <input
                                type="checkbox"
                                checked={securitySettings.twoFactorEnabled}
                                onChange={(e) => setSecuritySettings(prev => ({ ...prev, twoFactorEnabled: e.target.checked }))}
                                className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                            />
                        </label>
                    </div>

                    {securitySettings.twoFactorEnabled && (
                        <div className="bg-emerald-50 dark:bg-emerald-900/20 p-4 rounded-lg">
                            <p className="text-sm text-emerald-800 dark:text-emerald-200">
                                2FA is enabled. Users will need to verify their identity using an authenticator app when logging in.
                            </p>
                        </div>
                    )}
                </div>
            </motion.div>

            {/* reCAPTCHA Configuration */}
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700"
            >
                <div className="flex items-center space-x-3 mb-6">
                    <SafeIcon icon={FiShield} className="w-5 h-5 text-emerald-600" />
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        reCAPTCHA Configuration
                    </h3>
                </div>

                <div className="space-y-4">
                    <div className="flex items-center justify-between">
                        <div>
                            <h4 className="font-medium text-gray-900 dark:text-white">Enable reCAPTCHA</h4>
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                                Protect against automated attacks and spam
                            </p>
                        </div>
                        <label className="flex items-center">
                            <input
                                type="checkbox"
                                checked={securitySettings.recaptchaEnabled}
                                onChange={(e) => setSecuritySettings(prev => ({ ...prev, recaptchaEnabled: e.target.checked }))}
                                className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                            />
                        </label>
                    </div>

                    {securitySettings.recaptchaEnabled && (
                        <div className="grid md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Site Key
                                </label>
                                <input
                                    type="text"
                                    value={securitySettings.recaptchaSiteKey}
                                    onChange={(e) => setSecuritySettings(prev => ({ ...prev, recaptchaSiteKey: e.target.value }))}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                                    placeholder="Enter reCAPTCHA site key"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Secret Key
                                </label>
                                <input
                                    type="password"
                                    value={securitySettings.recaptchaSecretKey}
                                    onChange={(e) => setSecuritySettings(prev => ({ ...prev, recaptchaSecretKey: e.target.value }))}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                                    placeholder="Enter reCAPTCHA secret key"
                                />
                            </div>
                        </div>
                    )}
                </div>
            </motion.div>

            {/* Security Policies */}
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700"
            >
                <div className="flex items-center space-x-3 mb-6">
                    <SafeIcon icon={FiLock} className="w-5 h-5 text-emerald-600" />
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        Security Policies
                    </h3>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Session Timeout (minutes)
                        </label>
                        <input
                            type="number"
                            value={securitySettings.sessionTimeout}
                            onChange={(e) => setSecuritySettings(prev => ({ ...prev, sessionTimeout: parseInt(e.target.value) }))}
                            min="5"
                            max="480"
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Max Login Attempts
                        </label>
                        <input
                            type="number"
                            value={securitySettings.maxLoginAttempts}
                            onChange={(e) => setSecuritySettings(prev => ({ ...prev, maxLoginAttempts: parseInt(e.target.value) }))}
                            min="3"
                            max="10"
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Minimum Password Length
                        </label>
                        <input
                            type="number"
                            value={securitySettings.passwordMinLength}
                            onChange={(e) => setSecuritySettings(prev => ({ ...prev, passwordMinLength: parseInt(e.target.value) }))}
                            min="6"
                            max="20"
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                        />
                    </div>

                    <div>
                        <label className="flex items-center space-x-3">
                            <input
                                type="checkbox"
                                checked={securitySettings.requireSpecialChars}
                                onChange={(e) => setSecuritySettings(prev => ({ ...prev, requireSpecialChars: e.target.checked }))}
                                className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                            />
                            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                Require Special Characters in Passwords
                            </span>
                        </label>
                    </div>
                </div>
            </motion.div>

            {/* Save Button */}
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="flex justify-end"
            >
                <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={handleSecuritySettingsUpdate}
                    className="flex items-center space-x-2 px-6 py-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-semibold transition-colors"
                >
                    <SafeIcon icon={FiSave} className="w-4 h-4" />
                    <span>Save Security Settings</span>
                </motion.button>
            </motion.div>
        </div>
    );
};

export default SecuritySettings;